import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Phone, Mail, Calendar, AlertCircle, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';

const AlreadyHaveMedicare = () => {
  return (
    <div className="already-have-medicare-page">
      <SEO 
        title="Already Have Medicare"
        description="Already enrolled in Medicare? Get year-round support, annual plan reviews, and help switching plans. Free consultation to ensure your Medicare coverage is still right for you."
        keywords="Medicare support, Medicare help, change Medicare plans, Medicare consultation, Medicare advisor, Medicare assistance"
        path="/already-have-medicare"
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-teal-50 to-blue-50 py-16 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">Already have Medicare?</h1>
            <div className="bg-yellow-100 border-4 border-yellow-400 rounded-lg p-8 max-w-5xl mx-auto">
              <div className="flex items-center justify-center gap-4 mb-4">
                <AlertCircle className="text-yellow-700" size={48} />
                <h2 className="text-3xl font-bold text-gray-900">IMPORTANT MESSAGE</h2>
              </div>
              <p className="text-2xl font-bold text-gray-900 mb-3">
                Medicare Recommends an Annual Review for Every Senior
              </p>
              <p className="text-xl text-gray-800">
                Medicare plans change every year. Benefits can be lost, costs can increase, and your medications may no longer be covered. 
                A no-cost Annual Review informs and protects you—ensuring you have the coverage you need.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Annual Enrollment Period - Narrow Box */}
      <section className="py-8 px-4 bg-white">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-teal-100 border-4 border-teal-600 rounded-lg p-6">
            <div className="flex items-center justify-center gap-4 mb-3">
              <Calendar className="text-teal-700" size={36} />
              <h3 className="text-2xl font-bold text-gray-900">Annual Enrollment Period (AEP)</h3>
            </div>
            <p className="text-xl text-center font-bold text-gray-900">October 15 - December 7</p>
            <p className="text-lg text-center text-gray-800 mt-2">
              This is when you can make changes to your Medicare coverage for the following year
            </p>
          </div>
        </div>
      </section>

      {/* An Annual Review Is as Simple as 1, 2, 3! */}
      <section className="py-16 px-4 bg-gray-100">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-5xl font-bold text-center mb-12 text-gray-900">
            An Annual Review Is as Simple as 1, 2, 3!
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center mb-12">
            <Card className="border-4 border-teal-300 bg-white hover:shadow-2xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="text-7xl font-bold text-teal-600 mb-6">1</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Take the Survey</h3>
                <p className="text-lg text-gray-800 font-semibold mb-6">
                  Share your current plan, medications, and priorities
                </p>
                <Button asChild className="w-full bg-teal-600 hover:bg-teal-700 text-white text-lg py-6">
                  <a href="https://app.retireflo.com/paulette-lifeline-plans" target="_blank" rel="noopener noreferrer">
                    Start Survey
                  </a>
                </Button>
              </CardContent>
            </Card>

            <div className="hidden md:flex justify-center">
              <ArrowRight className="text-teal-600" size={64} />
            </div>

            <Card className="border-4 border-teal-300 bg-white hover:shadow-2xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="text-7xl font-bold text-teal-600 mb-6">2</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">We Research Your Options</h3>
                <p className="text-lg text-gray-800 font-semibold">
                  Personalized results delivered within 48 hours (per CMS rules)
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="max-w-xl mx-auto mb-12">
            <div className="hidden md:flex justify-center mb-8">
              <ArrowRight className="text-teal-600 transform rotate-90" size={64} />
            </div>
            <Card className="border-4 border-teal-300 bg-white hover:shadow-2xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="text-7xl font-bold text-teal-600 mb-6">3</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Schedule Your Call</h3>
                <p className="text-lg text-gray-800 font-semibold mb-6">
                  Let's talk through your options together—no pressure, just answers
                </p>
                <Button asChild className="w-full bg-teal-600 hover:bg-teal-700 text-white text-lg py-6">
                  <a href="https://calendly.com/llplans/30min" target="_blank" rel="noopener noreferrer">
                    Book Appointment
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700 text-white text-3xl px-16 py-10">
              <a href="https://app.retireflo.com/paulette-lifeline-plans" target="_blank" rel="noopener noreferrer">
                Start My Free Annual Review
              </a>
            </Button>
            <p className="text-lg text-gray-700 mt-4 font-semibold">
              Takes 5 minutes • No obligation • 100% free
            </p>
          </div>
        </div>
      </section>

      {/* Life Changes Throughout the Year */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-bold text-center mb-4 text-gray-900">
            LIFE CHANGES THROUGHOUT THE YEAR AND YOUR MEDICARE PLAN CAN TOO.
          </h2>
          <h3 className="text-3xl font-bold text-center mb-12 text-teal-600">DID YOU KNOW:</h3>

          <div className="space-y-6">
            <Card className="border-4 border-blue-300 bg-blue-50 hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <p className="text-2xl font-bold text-gray-900 mb-3">Moving?</p>
                <p className="text-xl text-gray-800">You may be able to change your plan</p>
              </CardContent>
            </Card>

            <Card className="border-4 border-green-300 bg-green-50 hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <p className="text-2xl font-bold text-gray-900 mb-3">Diagnosed with Diabetes, Heart Condition or ESRD?</p>
                <p className="text-xl text-gray-800">There are special plans created just for people with these chronic conditions</p>
              </CardContent>
            </Card>

            <Card className="border-4 border-purple-300 bg-purple-50 hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <p className="text-2xl font-bold text-gray-900 mb-3">Approved for Medicaid or Extra Help?</p>
                <p className="text-xl text-gray-800">This opens the door for a plan change</p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <p className="text-2xl font-bold text-gray-900 mb-6">
              Contact us to learn if you qualify for a Special Enrollment Period
            </p>
            <Button asChild size="lg" className="bg-teal-600 hover:bg-teal-700 text-white text-2xl px-12 py-8">
              <a href="tel:734.209.6712">
                <Phone className="mr-2" />
                Call: 734.209.6712
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-teal-50 to-blue-50">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-bold mb-6 text-gray-900">Questions About Your Current Coverage?</h2>
          <p className="text-2xl text-gray-800 mb-8">
            We're here to help you understand your options
          </p>
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-2 text-xl text-gray-800">
              <Mail size={24} />
              <span className="font-semibold">Email 24/7: <a href="mailto:Paulette@llplans.com" className="text-teal-600 hover:text-teal-700">Paulette@llplans.com</a></span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AlreadyHaveMedicare;
